import React, { useState, useEffect, useCallback } from "react";
import {
  Brain, Zap, Star, Target, AlertTriangle, CheckCircle, XCircle,
  RefreshCw, Clock, Play, Settings, ChevronRight, TrendingUp,
  TrendingDown, Shield, Globe, FileText, Link2, MapPin, Crosshair,
  ArrowUp, ArrowDown, Filter, Trophy,
} from "lucide-react";
import api from "../api/client";

// ─── helpers ─────────────────────────────────────────────────────
const scoreColor = (score) => {
  if (score >= 70) return "text-green-400 border-green-500/50 bg-green-900/20";
  if (score >= 50) return "text-yellow-400 border-yellow-500/50 bg-yellow-900/20";
  if (score >= 30) return "text-orange-400 border-orange-500/50 bg-orange-900/20";
  return "text-red-400 border-red-500/50 bg-red-900/20";
};

const priorityBadge = (p) => {
  const map = {
    critical: "bg-red-900/60 text-red-300 border border-red-700/40",
    high: "bg-orange-900/60 text-orange-300 border border-orange-700/40",
    medium: "bg-yellow-900/60 text-yellow-300 border border-yellow-700/40",
    low: "bg-green-900/60 text-green-300 border border-green-700/40",
  };
  return map[p] || map.medium;
};

const statusBadge = (s) => {
  const map = {
    new: "bg-blue-900/60 text-blue-300 border border-blue-700/40",
    in_progress: "bg-amber-900/60 text-amber-300 border border-amber-700/40",
    done: "bg-green-900/60 text-green-300 border border-green-700/40",
    dismissed: "bg-gray-800 text-gray-500 border border-gray-700/40",
    generating: "bg-blue-900/60 text-blue-300 border border-blue-700/40",
    generated: "bg-green-900/60 text-green-300 border border-green-700/40",
    failed: "bg-red-900/60 text-red-300 border border-red-700/40",
  };
  return map[s] || "bg-gray-800 text-gray-400";
};

const categoryIcon = (cat) => {
  const icons = {
    rankings: Target,
    content: FileText,
    gbp: MapPin,
    technical: Shield,
    competitors: Crosshair,
    backlinks: Link2,
  };
  return icons[cat] || Globe;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

// ─── TABS ────────────────────────────────────────────────────────
const TABS = ["Briefing", "Action Items", "History", "Settings"];

const MODULES = [
  { key: "rankings", label: "Rankings" },
  { key: "wordpress", label: "WordPress" },
  { key: "landing_pages", label: "Landing Pages" },
  { key: "competitors", label: "Competitors" },
  { key: "content", label: "Content" },
  { key: "backlinks", label: "Backlinks" },
  { key: "gbp", label: "Google Business" },
  { key: "technical", label: "Technical" },
];

// ─── main component ─────────────────────────────────────────────
export default function AIAgent({ selectedSite }) {
  const [tab, setTab] = useState("Briefing");
  const [analysis, setAnalysis] = useState(null);
  const [noAnalysis, setNoAnalysis] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [taskId, setTaskId] = useState(null);

  // Action items
  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");

  // History
  const [history, setHistory] = useState([]);
  const [historyCount, setHistoryCount] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Settings
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const siteId = selectedSite?.id;

  // Reset on site change
  useEffect(() => {
    setAnalysis(null);
    setNoAnalysis(false);
    setTab("Briefing");
    setActions([]);
    setHistory([]);
    setHistoryPage(1);
    setSettings(null);
    setTaskId(null);
    setAnalyzing(false);
  }, [siteId]);

  // ── fetch latest analysis ──────────────────────────────────────
  const fetchLatest = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const r = await api.get(`/ai-agent/latest/?site_id=${siteId}`);
      setAnalysis(r.data);
      setNoAnalysis(false);
    } catch (err) {
      if (err.response?.status === 404) {
        setNoAnalysis(true);
        setAnalysis(null);
      }
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  // ── fetch action items ─────────────────────────────────────────
  const fetchActions = useCallback(async () => {
    if (!siteId) return;
    setActionsLoading(true);
    const params = new URLSearchParams({ site_id: siteId });
    if (filterStatus !== "all") params.set("status", filterStatus);
    if (filterPriority !== "all") params.set("priority", filterPriority);
    if (filterCategory !== "all") params.set("category", filterCategory);
    try {
      const r = await api.get(`/ai-agent/actions/?${params}`);
      setActions(r.data || []);
    } catch {
      setActions([]);
    } finally {
      setActionsLoading(false);
    }
  }, [siteId, filterStatus, filterPriority, filterCategory]);

  useEffect(() => {
    if (tab === "Action Items") fetchActions();
  }, [tab, fetchActions]);

  // ── fetch history ──────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    if (!siteId) return;
    setHistoryLoading(true);
    try {
      const r = await api.get(`/ai-agent/history/?site_id=${siteId}&page=${historyPage}&page_size=20`);
      if (historyPage === 1) {
        setHistory(r.data.results || []);
      } else {
        setHistory((prev) => [...prev, ...(r.data.results || [])]);
      }
      setHistoryCount(r.data.count || 0);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [siteId, historyPage]);

  useEffect(() => {
    if (tab === "History") fetchHistory();
  }, [tab, fetchHistory]);

  // ── fetch settings ─────────────────────────────────────────────
  const fetchSettings = useCallback(async () => {
    if (!siteId) return;
    setSettingsLoading(true);
    try {
      const r = await api.get(`/ai-agent/settings/?site_id=${siteId}`);
      setSettings(r.data);
    } catch {
      setSettings(null);
    } finally {
      setSettingsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (tab === "Settings") fetchSettings();
  }, [tab, fetchSettings]);

  // ── run analysis ───────────────────────────────────────────────
  const runAnalysis = async () => {
    if (!siteId || analyzing) return;
    setAnalyzing(true);
    try {
      const r = await api.post("/ai-agent/analyze/", { site_id: siteId });
      setTaskId(r.data.task_id);
    } catch {
      setAnalyzing(false);
    }
  };

  // ── poll task status ───────────────────────────────────────────
  useEffect(() => {
    if (!taskId) return;
    const interval = setInterval(async () => {
      try {
        const r = await api.get(`/ai-agent/status/${taskId}/`);
        if (r.data.status === "SUCCESS") {
          clearInterval(interval);
          setTaskId(null);
          setAnalyzing(false);
          fetchLatest();
        } else if (r.data.status === "FAILURE") {
          clearInterval(interval);
          setTaskId(null);
          setAnalyzing(false);
        }
      } catch {
        clearInterval(interval);
        setTaskId(null);
        setAnalyzing(false);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [taskId, fetchLatest]);

  // ── update action status ───────────────────────────────────────
  const updateAction = async (actionId, status) => {
    try {
      await api.patch(`/ai-agent/actions/${actionId}/`, { status });
      setActions((prev) =>
        prev.map((a) => (a.id === actionId ? { ...a, status } : a))
      );
      // Also update inline preview in analysis
      if (analysis?.action_items) {
        setAnalysis((prev) => ({
          ...prev,
          action_items: prev.action_items.map((a) =>
            a.id === actionId ? { ...a, status } : a
          ),
        }));
      }
    } catch { /* silent */ }
  };

  // ── save settings ──────────────────────────────────────────────
  const saveSettings = async () => {
    if (!siteId || !settings) return;
    setSettingsSaving(true);
    try {
      const r = await api.put("/ai-agent/settings/update/", { site_id: siteId, ...settings });
      setSettings(r.data);
    } catch { /* silent */ }
    finally { setSettingsSaving(false); }
  };

  // ── loading state ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain size={24} className="text-copper" />
            <div>
              <h2 className="text-lg font-semibold">AI Agent</h2>
              <p className="text-sm text-gray-500">Centralized intelligence hub — powered by Claude</p>
            </div>
          </div>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-28 bg-dark-300 rounded-xl" />
            ))}
          </div>
          <div className="h-64 bg-dark-300 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-copper/10 rounded-lg">
            <Brain size={22} className="text-copper" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">AI Agent</h2>
            <p className="text-sm text-gray-500">Centralized intelligence hub — powered by Claude</p>
          </div>
        </div>
        <button
          onClick={runAnalysis}
          disabled={analyzing}
          className="flex items-center gap-2 px-4 py-2.5 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg disabled:opacity-60 transition-colors"
        >
          {analyzing ? (
            <RefreshCw size={16} className="animate-spin" />
          ) : (
            <Play size={16} />
          )}
          {analyzing ? "Analyzing..." : "Run Analysis"}
        </button>
      </div>

      {/* ── Empty State ─────────────────────────────────────────── */}
      {noAnalysis && !analyzing && (
        <div className="bg-dark-200 border border-dark-50 rounded-xl p-12 text-center">
          <Brain size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-300 mb-2">No analysis yet</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Click Run Analysis to get your first AI briefing. The agent will review your rankings,
            content, backlinks, and more to generate actionable insights.
          </p>
        </div>
      )}

      {/* ── Analyzing State ─────────────────────────────────────── */}
      {analyzing && (
        <div className="bg-dark-200 border border-copper/30 rounded-xl p-8 text-center">
          <RefreshCw size={36} className="text-copper animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-200 mb-1">Analysis in progress</h3>
          <p className="text-sm text-gray-500">The AI agent is reviewing your site data. This usually takes 1-2 minutes.</p>
        </div>
      )}

      {/* ── Main Content ────────────────────────────────────────── */}
      {analysis && !analyzing && (
        <>
          {/* Tabs */}
          <div className="flex gap-1 bg-dark-300 rounded-lg p-1 w-fit border border-dark-50">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  tab === t
                    ? "bg-dark-100 text-white shadow"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* ── Briefing Tab ────────────────────────────────────── */}
          {tab === "Briefing" && (
            <div className="space-y-6">
              {/* Score Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Health Score */}
                <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Health Score</span>
                    <Shield size={14} className="text-gray-600" />
                  </div>
                  <div className="flex items-end gap-2">
                    <span className={`text-3xl font-bold font-mono ${scoreColor(analysis.health_score).split(" ")[0]}`}>
                      {analysis.health_score}
                    </span>
                    {analysis.health_change !== 0 && analysis.health_change != null && (
                      <span className={`flex items-center gap-0.5 text-xs font-mono mb-1 px-1.5 py-0.5 rounded ${
                        analysis.health_change > 0
                          ? "text-green-400 bg-green-900/30"
                          : "text-red-400 bg-red-900/30"
                      }`}>
                        {analysis.health_change > 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                        {analysis.health_change > 0 ? "+" : ""}{analysis.health_change}
                      </span>
                    )}
                  </div>
                </div>

                {/* Content Score */}
                <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Content Score</span>
                    <FileText size={14} className="text-gray-600" />
                  </div>
                  <span className={`text-3xl font-bold font-mono ${scoreColor(analysis.content_score).split(" ")[0]}`}>
                    {analysis.content_score}
                  </span>
                </div>

                {/* Page 1 Rankings */}
                <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Page 1 Rankings</span>
                    <TrendingUp size={14} className="text-gray-600" />
                  </div>
                  <span className="text-3xl font-bold font-mono text-copper">
                    {analysis.rankings_page1_pct != null ? `${analysis.rankings_page1_pct.toFixed(1)}%` : "—"}
                  </span>
                </div>

                {/* Status */}
                <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Status</span>
                    <Clock size={14} className="text-gray-600" />
                  </div>
                  <span className={`inline-block px-2.5 py-1 rounded-lg text-sm font-medium ${statusBadge(analysis.status)}`}>
                    {analysis.status}
                  </span>
                  <div className="text-xs text-gray-500 mt-2">{formatDate(analysis.created_at)}</div>
                </div>
              </div>

              {/* Focus This Week */}
              {analysis.focus_this_week && (
                <div className="bg-dark-200 border border-copper/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Target size={16} className="text-copper" />
                    <h3 className="text-sm font-semibold text-copper uppercase tracking-wide">Focus This Week</h3>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed">{analysis.focus_this_week}</p>
                </div>
              )}

              {/* Quick Wins */}
              {analysis.quick_wins_json?.length > 0 && (
                <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap size={16} className="text-yellow-400" />
                    <h3 className="text-sm font-semibold uppercase tracking-wide">Quick Wins</h3>
                  </div>
                  <ul className="space-y-2">
                    {analysis.quick_wins_json.map((win, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                        <Zap size={14} className="text-yellow-400 mt-0.5 shrink-0" />
                        <span>{win}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Wins */}
              {analysis.wins_json?.length > 0 && (
                <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy size={16} className="text-green-400" />
                    <h3 className="text-sm font-semibold uppercase tracking-wide">Recent Wins</h3>
                  </div>
                  <div className="space-y-3">
                    {analysis.wins_json.map((win, i) => {
                      const CatIcon = categoryIcon(win.category);
                      return (
                        <div key={i} className="flex items-start gap-3 bg-dark-300 rounded-lg p-3">
                          <Star size={14} className="text-green-400 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {win.category && (
                                <span className="flex items-center gap-1 text-[10px] text-gray-500 uppercase tracking-wider">
                                  <CatIcon size={10} />
                                  {win.category}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-200 font-medium">{win.title}</div>
                            {win.details && <div className="text-xs text-gray-500 mt-0.5">{win.details}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Top Action Items Preview */}
              {analysis.action_items?.length > 0 && (
                <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={16} className="text-amber-400" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide">Top Action Items</h3>
                    </div>
                    <span className="text-xs text-gray-500">{analysis.action_items.length} total</span>
                  </div>
                  <div className="space-y-3">
                    {analysis.action_items.slice(0, 5).map((item) => (
                      <ActionItemCard key={item.id} item={item} onUpdate={updateAction} compact />
                    ))}
                  </div>
                  {analysis.action_items.length > 5 && (
                    <button
                      onClick={() => setTab("Action Items")}
                      className="flex items-center gap-1 mt-4 text-sm text-copper hover:text-copper-600 transition-colors"
                    >
                      View All Actions <ChevronRight size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Action Items Tab ────────────────────────────────── */}
          {tab === "Action Items" && (
            <div className="space-y-4">
              {/* Filter Bar */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5 text-sm text-gray-500">
                  <Filter size={14} />
                  <span>Filters:</span>
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-copper/50"
                >
                  <option value="all">All Statuses</option>
                  <option value="new">New</option>
                  <option value="in_progress">In Progress</option>
                  <option value="done">Done</option>
                  <option value="dismissed">Dismissed</option>
                </select>
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-copper/50"
                >
                  <option value="all">All Priorities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-sm text-gray-300 focus:outline-none focus:border-copper/50"
                >
                  <option value="all">All Categories</option>
                  <option value="rankings">Rankings</option>
                  <option value="content">Content</option>
                  <option value="gbp">GBP</option>
                  <option value="technical">Technical</option>
                  <option value="competitors">Competitors</option>
                  <option value="backlinks">Backlinks</option>
                </select>
              </div>

              {/* Action Items List */}
              {actionsLoading ? (
                <div className="flex items-center justify-center h-48">
                  <RefreshCw size={24} className="text-copper animate-spin" />
                </div>
              ) : actions.length === 0 ? (
                <div className="bg-dark-200 border border-dark-50 rounded-xl p-8 text-center">
                  <CheckCircle size={32} className="text-green-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">No action items match your filters.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {actions.map((item) => (
                    <ActionItemCard key={item.id} item={item} onUpdate={updateAction} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── History Tab ─────────────────────────────────────── */}
          {tab === "History" && (
            <div className="space-y-4">
              {historyLoading && history.length === 0 ? (
                <div className="flex items-center justify-center h-48">
                  <RefreshCw size={24} className="text-copper animate-spin" />
                </div>
              ) : history.length === 0 ? (
                <div className="bg-dark-200 border border-dark-50 rounded-xl p-8 text-center">
                  <Clock size={32} className="text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-400">No past analyses found.</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {history.map((h) => (
                      <div
                        key={h.id}
                        className="bg-dark-200 border border-dark-50 rounded-xl p-4 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`text-2xl font-bold font-mono ${scoreColor(h.health_score).split(" ")[0]}`}>
                            {h.health_score}
                          </div>
                          <div>
                            <div className="text-sm text-gray-300">{formatDate(h.created_at)}</div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${statusBadge(h.status)}`}>
                                {h.status}
                              </span>
                              {h.health_change != null && h.health_change !== 0 && (
                                <span className={`flex items-center gap-0.5 text-xs font-mono ${
                                  h.health_change > 0 ? "text-green-400" : "text-red-400"
                                }`}>
                                  {h.health_change > 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                                  {h.health_change > 0 ? "+" : ""}{h.health_change}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-gray-600" />
                      </div>
                    ))}
                  </div>
                  {history.length < historyCount && (
                    <div className="text-center">
                      <button
                        onClick={() => setHistoryPage((p) => p + 1)}
                        disabled={historyLoading}
                        className="px-4 py-2 text-sm text-gray-400 hover:text-white bg-dark-300 border border-dark-50 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {historyLoading ? "Loading..." : "Load More"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Settings Tab ────────────────────────────────────── */}
          {tab === "Settings" && (
            <div className="space-y-6">
              {settingsLoading ? (
                <div className="flex items-center justify-center h-48">
                  <RefreshCw size={24} className="text-copper animate-spin" />
                </div>
              ) : settings ? (
                <>
                  {/* Auto-analyze toggle */}
                  <div className="bg-dark-200 border border-dark-50 rounded-xl p-5 space-y-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Settings size={16} className="text-gray-500" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide">Schedule</h3>
                    </div>
                    <label className="flex items-start justify-between cursor-pointer group">
                      <div>
                        <div className="text-sm text-gray-200 group-hover:text-white">Auto-analyze</div>
                        <div className="text-xs text-gray-500 mt-0.5">Automatically run AI analysis on a schedule</div>
                      </div>
                      <div
                        className={`relative w-10 h-5 rounded-full transition-colors ${settings.auto_analyze ? "bg-copper" : "bg-dark-50"}`}
                        onClick={() => setSettings((s) => ({ ...s, auto_analyze: !s.auto_analyze }))}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${settings.auto_analyze ? "translate-x-5" : "translate-x-0.5"}`} />
                      </div>
                    </label>

                    {/* Frequency */}
                    <div>
                      <label className="text-sm text-gray-400 block mb-1.5">Frequency</label>
                      <select
                        value={settings.frequency || "weekly"}
                        onChange={(e) => setSettings((s) => ({ ...s, frequency: e.target.value }))}
                        className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none focus:border-copper/50 w-48"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    {/* Email notifications */}
                    <label className="flex items-start justify-between cursor-pointer group">
                      <div>
                        <div className="text-sm text-gray-200 group-hover:text-white">Email notifications</div>
                        <div className="text-xs text-gray-500 mt-0.5">Receive analysis results via email</div>
                      </div>
                      <div
                        className={`relative w-10 h-5 rounded-full transition-colors ${settings.notify_email ? "bg-copper" : "bg-dark-50"}`}
                        onClick={() => setSettings((s) => ({ ...s, notify_email: !s.notify_email }))}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${settings.notify_email ? "translate-x-5" : "translate-x-0.5"}`} />
                      </div>
                    </label>
                  </div>

                  {/* Modules */}
                  <div className="bg-dark-200 border border-dark-50 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Globe size={16} className="text-gray-500" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide">Analysis Modules</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {MODULES.map((mod) => {
                        const enabled = settings[mod.key] !== false;
                        return (
                          <label
                            key={mod.key}
                            className={`flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${
                              enabled
                                ? "bg-copper/10 border-copper/30 text-gray-200"
                                : "bg-dark-300 border-dark-50 text-gray-500"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={enabled}
                              onChange={() => setSettings((s) => ({ ...s, [mod.key]: !enabled }))}
                              className="sr-only"
                            />
                            <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                              enabled ? "bg-copper border-copper" : "border-gray-600"
                            }`}>
                              {enabled && <CheckCircle size={10} className="text-white" />}
                            </div>
                            <span className="text-sm">{mod.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Save */}
                  <div className="flex justify-end">
                    <button
                      onClick={saveSettings}
                      disabled={settingsSaving}
                      className="flex items-center gap-2 px-5 py-2.5 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg disabled:opacity-60 transition-colors"
                    >
                      {settingsSaving ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <CheckCircle size={14} />
                      )}
                      {settingsSaving ? "Saving..." : "Save Settings"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="bg-dark-200 border border-dark-50 rounded-xl p-8 text-center">
                  <p className="text-sm text-gray-400">Unable to load settings.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Action Item Card ────────────────────────────────────────────
function ActionItemCard({ item, onUpdate, compact = false }) {
  const CatIcon = categoryIcon(item.category);

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Priority + Category + Status */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${priorityBadge(item.priority)}`}>
              {item.priority}
            </span>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-dark-200 text-[10px] text-gray-400 uppercase tracking-wider">
              <CatIcon size={10} />
              {item.category}
            </span>
            {item.status !== "new" && (
              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${statusBadge(item.status)}`}>
                {item.status.replace("_", " ")}
              </span>
            )}
          </div>

          {/* Title */}
          <h4 className="text-sm font-medium text-gray-200">{item.title}</h4>

          {/* Details (hidden in compact) */}
          {!compact && item.details && (
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">{item.details}</p>
          )}

          {/* Affected Items */}
          {!compact && item.affected_items_json?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {item.affected_items_json.map((ai, i) => (
                <span key={i} className="px-2 py-0.5 rounded bg-dark-200 text-[10px] text-gray-500 font-mono">
                  {ai}
                </span>
              ))}
            </div>
          )}

          {/* Recommended Action */}
          {!compact && item.recommended_action && (
            <div className="mt-2 flex items-start gap-2">
              <Zap size={12} className="text-copper mt-0.5 shrink-0" />
              <span className="text-xs text-gray-400">{item.recommended_action}</span>
            </div>
          )}

          {/* Estimated Impact */}
          {!compact && item.estimated_impact && (
            <div className="mt-1 flex items-start gap-2">
              <TrendingUp size={12} className="text-green-400 mt-0.5 shrink-0" />
              <span className="text-xs text-gray-500">{item.estimated_impact}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {(item.status === "new" || item.status === "in_progress") && (
          <div className="flex gap-1.5 shrink-0">
            {item.status === "new" && (
              <button
                onClick={() => onUpdate(item.id, "in_progress")}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-copper/20 text-copper rounded-lg hover:bg-copper/30 transition-colors"
                title="Start"
              >
                <Play size={11} /> Start
              </button>
            )}
            {item.status === "in_progress" && (
              <button
                onClick={() => onUpdate(item.id, "done")}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-green-900/30 text-green-400 rounded-lg hover:bg-green-900/50 transition-colors"
                title="Done"
              >
                <CheckCircle size={11} /> Done
              </button>
            )}
            <button
              onClick={() => onUpdate(item.id, "dismissed")}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-dark-200 text-gray-500 rounded-lg hover:text-gray-300 transition-colors"
              title="Dismiss"
            >
              <XCircle size={11} /> Dismiss
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
